/**
 * Mall Map — wayfinding for the SAMPLE estate.
 *
 * Builds a walkable graph from the boulevard centrelines, snaps every unit and
 * amenity onto it, links floors through the escalator/lift cores, then hands it
 * to the shared router in ./router for Dijkstra and narration.
 *
 * The graph is derived from the same geometry the 3D scene draws, so a route
 * can never cut through a shopfront — it always runs down the middle of a
 * corridor and enters a unit at its door.
 *
 * An imported floor plan has no corridor model to derive this from, so it
 * builds its graph out of traced circulation instead — see
 * Importer/planRouting.js. Both end up behind the same router interface.
 */

import { UNITS, AMENITIES, FLOORS, ZONES, H_CORRIDORS, SPINE, H_SPAN } from './mallData';
import { makeGraph, createRouter, nodeKey } from './router';

const key = nodeKey;

/** Vertical circulation cores — the only way between floors. */
const CORES = AMENITIES.filter((a) => a.type === 'Escalator' || a.type === 'Lift');

function buildGraph() {
    const graph = makeGraph();
    const { nodes, addNode, addEdge } = graph;

    FLOORS.forEach((floor) => {
        const onFloor = UNITS.filter((u) => u.floor === floor.id);

        // One chain of nodes per arcade centreline. Seed each with its ends, the
        // spine junction, and every court centre it passes, then add a node for
        // each shopfront's projection so doors attach at the right place.
        const arcadeXs = new Map();   // corridor z -> Set of x
        H_CORRIDORS.forEach((c) => {
            const set = new Set([H_SPAN.from, H_SPAN.to, 0]);
            ZONES.forEach((zone) => { if (Math.abs(zone.z - c.z) < 1) set.add(zone.x); });
            arcadeXs.set(c.z, set);
        });
        // Spine chain: its ends, plus every arcade junction.
        const spineZs = new Set([SPINE.from, SPINE.to]);
        H_CORRIDORS.forEach((c) => spineZs.add(c.z));

        onFloor.forEach((u) => {
            const pos = u.corridor ? u.corridor.pos : 0;
            if (arcadeXs.has(pos)) arcadeXs.get(pos).add(+u.frontage.x.toFixed(1));
            else spineZs.add(+u.frontage.z.toFixed(1));
        });

        AMENITIES.forEach((a) => {
            if (!a.floors.includes(floor.id)) return;
            if (arcadeXs.has(a.z)) arcadeXs.get(a.z).add(+a.x.toFixed(1));
            else if (Math.abs(a.x) < 1) spineZs.add(+a.z.toFixed(1));
            else arcadeXs.get(H_CORRIDORS[1].z).add(+a.x.toFixed(1));
        });

        // Chain each arcade.
        arcadeXs.forEach((set, z) => {
            const xs = [...set].sort((a, b) => a - b);
            let prev = null;
            xs.forEach((x) => {
                const n = addNode(floor.id, x, z, 'corridor');
                if (prev) addEdge(prev, n, 'walk');
                prev = n;
            });
        });

        // Chain the spine, which also stitches the arcades together.
        const zs = [...spineZs].sort((a, b) => a - b);
        let prev = null;
        zs.forEach((z) => {
            const n = addNode(floor.id, 0, z, 'corridor');
            if (prev) addEdge(prev, n, 'walk');
            prev = n;
        });

        // Doors hang off their own corridor's centreline.
        onFloor.forEach((u) => {
            const door = addNode(floor.id, u.frontage.x, u.frontage.z, 'door', u.name);
            door.unitId = u.id;
            const pos = u.corridor ? u.corridor.pos : 0;
            const spur = arcadeXs.has(pos)
                ? addNode(floor.id, u.frontage.x, pos, 'corridor')
                : addNode(floor.id, 0, u.frontage.z, 'corridor');
            addEdge(door, spur, 'walk');
        });

        // Amenity markers spur off the nearest centreline.
        AMENITIES.forEach((a) => {
            if (!a.floors.includes(floor.id)) return;
            const n = addNode(floor.id, a.x, a.z, 'amenity', a.name);
            n.amenityId = a.id;
            n.amenityType = a.type;
            const spur = arcadeXs.has(a.z)
                ? addNode(floor.id, a.x, a.z, 'corridor')
                : Math.abs(a.x) < 1
                    ? addNode(floor.id, 0, a.z, 'corridor')
                    : addNode(floor.id, a.x, H_CORRIDORS[1].z, 'corridor');
            addEdge(n, spur, 'walk');
        });
    });

    // Stitch floors together at the cores.
    CORES.forEach((core) => {
        const stack = FLOORS.filter((f) => core.floors.includes(f.id));
        for (let i = 0; i < stack.length - 1; i++) {
            const a = nodes.get(key(stack[i].id, core.x, core.z));
            const b = nodes.get(key(stack[i + 1].id, core.x, core.z));
            if (a && b) addEdge(a, b, 'vertical');
        }
    });

    return graph;
}

const GRAPH = buildGraph();

/** Route endpoints the picker offers: every unit, plus every amenity instance. */
export function routeEndpoints() {
    const unitPoints = UNITS.map((u) => ({
        id: `unit:${u.id}`,
        label: u.name,
        sub: `${u.code} · ${u.floorName}`,
        floor: u.floor,
        nodeKey: key(u.floor, u.frontage.x, u.frontage.z),
        kind: 'unit',
    }));
    const amenityPoints = [];
    AMENITIES.forEach((a) => {
        a.floors.forEach((f) => {
            const floor = FLOORS.find((x) => x.id === f);
            amenityPoints.push({
                id: `amenity:${a.id}:${f}`,
                label: a.name,
                sub: `${a.type} · ${floor ? floor.name : f}`,
                floor: f,
                nodeKey: key(f, a.x, a.z),
                kind: 'amenity',
            });
        });
    });
    return [...unitPoints, ...amenityPoints];
}

const nearestZoneName = (pt) => {
    let best = null;
    let bestD = Infinity;
    ZONES.forEach((zone) => {
        const d = Math.hypot(zone.x - pt.x, zone.z - pt.z);
        if (d < bestD) { bestD = d; best = zone; }
    });
    return best && bestD <= best.r + 18 ? best.name : null;
};

const ROUTER = createRouter({
    graph: GRAPH,
    endpoints: routeEndpoints(),
    floors: FLOORS,
    landmarkAt: nearestZoneName,
});

export const getEndpoint = (id) => ROUTER.getEndpoint(id);
export const endpointForUnit = (unitId) => ROUTER.getEndpoint(`unit:${unitId}`);
export const findRoute = (fromId, toId) => ROUTER.findRoute(fromId, toId);
export default ROUTER;
